import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { InputGroup } from "@/components/ui/input-group";
import { Text } from "@/components/ui/text";
import { AppDrawer, Loading } from "@components";
import LayoutHeader from "@components/LayoutHeader";
import { useSearchParamSync } from "@components/PageSearch";
import Panel from "@components/Panel";
import { userId } from "@state/auth";
import { useGoRead } from "@state/goRead/state";
import { Plus, Search } from "lucide-solid";
import { For, Show, createMemo } from "solid-js";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { scrollbar } from "styled-system/recipes";
import { TOPICS, TOPIC_KEYS, pill } from "@features/communityContent/topics";
import { segButton, segTrack } from "../../Setups/List/Header/segStyles";
import Create from "./Create";
import Row from "./Row";
import { type GoReadSort, useState } from "./state";

const SORTS: { value: GoReadSort; label: string }[] = [
  { value: 'latest', label: 'Latest' },
  { value: 'discussed', label: 'Discussed' },
];

export default function () {
  const { postsQuery, posts } = useService(useGoRead)();
  const {
    isCreateFormOpen, setIsCreateFormOpen, editPost,
    sort, setSort, topicFilter, setTopicFilter, search, setSearch,
  } = useService(useState)();

  // Arriving from global search ("Open in Go Read") shows every topic so no match is hidden
  useSearchParamSync(search, (q) => {
    setSearch(q);
    if (q) setTopicFilter('all');
  });

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    const scope = topicFilter();
    return posts().filter((p) => {
      if (scope === 'mine') {
        if (p.profile_id !== userId()) return false;
      } else if (scope !== 'all' && p.topic !== scope) {
        return false;
      }
      if (q && !(`${p.title} ${p.author_username ?? ''} ${p.content ?? ''}`).toLowerCase().includes(q)) return false;
      return true;
    });
  });

  const sorted = createMemo(() => {
    const list = filtered().slice();
    if (sort() === 'discussed') {
      list.sort((a, b) =>
        (b.comment_count - a.comment_count) ||
        (new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
      );
    } else {
      list.sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime());
    }
    return list;
  });

  return (
    <Grid gridTemplateRows='auto auto 1fr' gap='0'>
      <LayoutHeader
        leftContent={
          <Flex alignItems='baseline' gap='2' flexWrap='wrap'>
            <Heading fontSize='21px' fontWeight='800' letterSpacing='-.02em' color='editorialInk'>
              Go Read
            </Heading>
            <Text display={{ base: 'inline', smDown: 'none' }} color='editorialInk3' fontSize='12px'>
              Community reading library
            </Text>
          </Flex>
        }
        rightContent={
          <Flex gap='2' alignItems='center' flexWrap='nowrap'>
            <InputGroup
              size='sm'
              startElement={<Search size={15} color='var(--colors-editorial-ink3)' />}
              class={css({ w: { base: '190px', mdDown: '150px', smDown: '120px' }, flexShrink: 1 })}
            >
              <Input
                size='sm'
                placeholder='Search articles…'
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
                borderRadius='9px'
                borderColor='var(--colors-editorial-hair-strong)'
                bg='editorialPanel'
                color='editorialInk'
                class={css({ _placeholder: { color: 'editorialInk3' } })}
              />
            </InputGroup>

            <AppDrawer
              open={isCreateFormOpen()}
              onOpenChange={({ open }) => setIsCreateFormOpen(open)}
              title={editPost() ? 'Edit Post' : 'Write a post'}
              size='md'
              trigger={
                <Button
                  size='sm'
                  bg='editorialAccent'
                  color='editorialBadgeFg'
                  fontWeight='700'
                  fontSize='12.5px'
                  borderRadius='9px'
                  px='15px'
                  gap='7px'
                  flexShrink={0}
                  class={css({ _hover: { filter: 'brightness(1.04)', bg: 'editorialAccent' } })}
                >
                  <Plus size={15} />
                  <Text display={{ base: 'inline', smDown: 'none' }}>Submit article</Text>
                </Button>
              }
            >
              <Create onSuccess={() => setIsCreateFormOpen(false)} />
            </AppDrawer>
          </Flex>
        }
      />

      <Flex
        alignItems='center'
        gap='12px'
        flexWrap='wrap'
        borderBottomWidth='1px'
        borderBottomStyle='solid'
        borderBottomColor='var(--colors-editorial-hair)'
        px={{ base: '22px', smDown: '12px' }}
        py='10px'
      >
        <Flex role='group' aria-label='Filter by topic' gap='7px' alignItems='center' flexWrap='wrap'>
          <button type='button' class={pill(topicFilter() === 'all')} onClick={() => setTopicFilter('all')}>
            All
          </button>
          <For each={TOPIC_KEYS}>
            {(key) => (
              <button
                type='button'
                class={pill(topicFilter() === key)}
                onClick={() => setTopicFilter(key)}
              >
                <Box
                  as='span'
                  display='inline-block'
                  w='7px'
                  h='7px'
                  borderRadius='full'
                  mr='6px'
                  style={{ background: TOPICS[key].color }}
                />
                {TOPICS[key].label}
              </button>
            )}
          </For>
          <button type='button' class={pill(topicFilter() === 'mine')} onClick={() => setTopicFilter('mine')}>
            Mine
          </button>
        </Flex>

        <Box flex='1' />

        <Text fontSize='11px' fontWeight='600' letterSpacing='.04em' textTransform='uppercase' color='editorialInk3'>
          Sort
        </Text>
        <div role='group' aria-label='Sort' class={segTrack}>
          <For each={SORTS}>
            {(s) => (
              <button
                type='button'
                aria-pressed={sort() === s.value}
                class={segButton(sort() === s.value)}
                onClick={() => setSort(s.value)}
              >
                {s.label}
              </button>
            )}
          </For>
        </div>
      </Flex>

      <Panel
        display='grid'
        gap='12px'
        height={{
          base: 'calc(var(--vh) * 100 - 130px)',
          mdDown: 'calc(var(--vh) * 100 - 160px)'
        }}
        overflowY='auto'
        class={scrollbar()}
        alignContent='start'
        gridAutoRows='max-content'
      >
        <Show when={postsQuery.isPending}>
          <Loading />
        </Show>

        <Show when={postsQuery.isError}>
          <Text color="error.10">Failed to load articles.</Text>
        </Show>

        <Text fontSize='12px' color='editorialInk3' m='2px 2px 2px'>
          {sorted().length} article{sorted().length === 1 ? '' : 's'}
          {(() => {
            const t = topicFilter();
            if (t === 'all') return '';
            if (t === 'mine') return ' by you';
            return ` in ${TOPICS[t].label}`;
          })()}
        </Text>

        <For each={sorted()}>
          {(post) => <Row post={post} />}
        </For>

        <Show when={!postsQuery.isPending && sorted().length === 0}>
          <Text color="fg.muted" textAlign="center" py="8">
            Nothing here yet. Try a different topic, or be the first to write something.
          </Text>
        </Show>
      </Panel>
    </Grid>
  )
}
